'use client';

import { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { cn } from '@/utils/cn';

interface ProcessStageVideoProps {
  src: string;
  isActive: boolean;
}

export default function ProcessStageVideo({ src, isActive }: ProcessStageVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    video.muted = true;
    video.volume = 0;

    if (isActive) {
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          /* Autoplay can be blocked until user gesture — retry once loaded */
        });
      }
    } else {
      video.pause();
    }
  }, [isActive, src]);

  if (!src) return null;

  return (
    <div className="process-stage-video relative h-full w-full overflow-hidden">
      <NextImage
        src={MEDIA_PLACEHOLDER}
        alt=""
        aria-hidden
        fill
        unoptimized
        className="object-cover"
        sizes="100vw"
      />
      <video
        ref={videoRef}
        src={src}
        autoPlay={isActive}
        loop
        muted
        playsInline
        preload="auto"
        poster={MEDIA_PLACEHOLDER}
        className={cn(
          'process-stage-video__media absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
          isReady ? 'opacity-100' : 'opacity-0',
        )}
        onLoadedData={() => setIsReady(true)}
        onCanPlay={() => setIsReady(true)}
        onError={() => setIsReady(false)}
      />
    </div>
  );
}
